import * as React from 'react';
import {useContext, useEffect, useState, useRef, useReducer} from 'react';
import {UserContext, withUser} from "../context/UserContext";
import {Auth} from "../util/auth";

import "./LandingPage.css";
import {Rest} from "../util/rest";
import moment from "moment";
import {toast} from "react-toastify";
import {Button} from "@mui/material";
import {useParams} from "react-router";
import {useNavigate} from "react-router-dom";
import AssessmentV2 from "./assessmentV2/AssessmentV2";
import DOMPurify from 'dompurify';
import ExcelSurveyUpload from "./ExcelSurveyUpload";

// Request state machine to eliminate timing race conditions
const REQUEST_STATES = {
    IDLE: 'idle',
    LOADING: 'loading',
    SUCCESS: 'success',
    ERROR: 'error',
};

const assessmentReducer = (state, action) => {
    switch (action.type) {
        case 'FETCH_START':
            return { ...state, requestState: REQUEST_STATES.LOADING, requestId: action.requestId, error: null };
        case 'FETCH_SUCCESS':
            return { ...state, requestState: REQUEST_STATES.SUCCESS, batchAssessment: action.payload, requestId: action.requestId, error: null };
        case 'FETCH_ERROR':
            return { ...state, requestState: REQUEST_STATES.ERROR, batchAssessment: null, requestId: action.requestId, error: action.error };
        case 'RESET':
            return { requestState: REQUEST_STATES.IDLE, batchAssessment: null, requestId: null, error: null };
        default:
            return state;
    }
};

const LandingPage = () => {
    const { urlKey } = useParams();
    const {user, setUser} = useContext(UserContext);
    
    const [assessmentState, dispatch] = useReducer(assessmentReducer, {
        requestState: REQUEST_STATES.IDLE,
        batchAssessment: null,
        requestId: null,
        error: null,
    });
    
    const navigate   = useNavigate();
    const reportTypeRef = useRef(null);
    const assignmentIdRef = useRef(null);
    const currentRequestIdRef = useRef(null);
    
    const [assessment, setAssessment] = useState(null);
    const [showAssessment, setShowAssessment] = useState(false);
    const [shouldShowAssessment, setShouldShowAssessment] = useState(false);
    const [showThankYou, setShowThankYou] = useState(false);
    const [reload, setReload] = useState(false);

    useEffect(() => {
        if(!user && urlKey) {
            Auth.login(urlKey)
                    .then((authResult) => {
                        if(authResult.authenticated) {
                            setUser(authResult.user);
                        }
                        else {
                            // Auth failed - set user to null and show error
                            setUser(null);
                            // Dispatch error state to show "Assessment not available"
                            dispatch({ type: 'FETCH_ERROR', error: 'Assessment not available', requestId: null });
                        }
                    });
        }
    }, [setUser, urlKey, user])    //Authenticate using UUID

    useEffect(() => {
        if (!user || !urlKey) {
            dispatch({ type: 'RESET' });
            return;
        }

        // Only fetch if urlKey changed or component remounted
        if (assessmentState.batchAssessment?.urlKey === urlKey) {
            return;
        }

        const requestId = Math.random();
        currentRequestIdRef.current = requestId;
        dispatch({ type: 'FETCH_START', requestId });

        const fetchAssessment = async () => {
            try {
                const response = await Rest.authFetch(user, "/rest/assessment/batch/" + urlKey, {
                    headers: {
                        accept: 'application/json'
                    }
                });

                // Ignore stale responses from previous requests
                if (requestId === currentRequestIdRef.current) {
                    if (response) {
                        dispatch({ type: 'FETCH_SUCCESS', payload: response, requestId });
                        setAssessment(response);
                    } else {
                        // null response means non-200 status
                        dispatch({ type: 'FETCH_ERROR', error: 'Assessment not found', requestId });
                    }
                }
            } catch (err) {
                console.error('Assessment fetch error:', err);
                // Only update state if this is still the current request
                if (requestId === currentRequestIdRef.current) {
                    dispatch({ type: 'FETCH_ERROR', error: 'Assessment not available', requestId });
                }
            }
        };

        fetchAssessment();
    }, [user, urlKey, assessmentState.batchAssessment?.urlKey]);

    useEffect(() => {

        if(shouldShowAssessment && assessmentState.batchAssessment) {
            if(assessmentState.batchAssessment.assignmentType === "ANONYMOUS") {
                Rest.authFetch(user, `/rest/assessments/batch/clone/${assessmentState.batchAssessment.id}`)
                        .then(response => {
                            if(response) {
                                reportTypeRef.current = response.template?.reportType ?? null;
                                assignmentIdRef.current = response.id; 
                                setAssessment(response);
                                setShowAssessment(true);
                            }
                        });
            }
            else {
                setAssessment(assessmentState.batchAssessment);
                setShowAssessment(true);
            }
        }
        else
        setShowAssessment(false)
    }, [assessmentState.batchAssessment, shouldShowAssessment, user])  //Clone assignment if anonymous           

    useEffect(() => {
        setReload(false);
    }, [reload]);


    function save(assessment, newRecord = false) {
        Rest.authFetch(user, "/rest/assessment", {
            method: "PUT",
            body: JSON.stringify(assessment)
        })
                .then(response => {
                    if(response) {
                        if(response && response.dateEnded !== null) { toast.success("Assessment submitted");}
                        
                        if (newRecord && reportTypeRef.current === "IQOL") {
                            navigate(`/iqol-report/${assignmentIdRef.current}`);
                            return;
                        } else if (newRecord && (isThankYouReport || isIpathReport)) {
                            // Show thank-you page for THANK_YOU and IPATH flows and do not reload the assignment list
                            setShowAssessment(false);
                            setShouldShowAssessment(false);
                            setShowThankYou(true);
                            return;
                        }
                        setReload(true);
                    }
                })
                .catch(err => {
                    console.log(err);

                });
    }


    function handleClose(saveAssessment, assessment, newRecord = false) {
        if (saveAssessment) {
            const goingToIqol = newRecord && reportTypeRef.current === "IQOL";
            const goingToThankYou = newRecord && reportTypeRef.current === "THANK_YOU";
            const goingToIpath = newRecord && (reportTypeRef.current === "IPATH" || reportTypeRef.current?.includes("IPATH_"));

            if (!goingToIqol && !goingToThankYou && !goingToIpath) {
                setShowAssessment(false);
                setShouldShowAssessment(false);
            }

            save(assessment, newRecord);
        } else {
            setShowAssessment(false);
            setShouldShowAssessment(false);
        }
    }

    function handleExcelUploaded() {
        setShowAssessment(false);
        setShouldShowAssessment(false);
        setShowThankYou(true);
    }

    function handleCompleteAnotherAssessment() {
        setShowThankYou(false);
        setShowAssessment(false);
        setShouldShowAssessment(false);
        setReload(true);
    }

    const isThankYouReport = reportTypeRef.current === "THANK_YOU";
    const isIpathReport = reportTypeRef.current === "IPATH" || reportTypeRef.current?.includes("IPATH_");
    const isExcelReport = assessmentState.batchAssessment?.template?.reportType === "EXCEL";

    return (
            <div className="landing-page">
                                {showAssessment && assessment ?
                 <AssessmentV2 assessment={assessment} handleClose={handleClose}/>
                                              :
                 <>
                                         <div className="row header">
                         <div className="col-sm-4 col-md-3 branding">
                             <img id="logoImpactivo" src="/assets/media/logoimpactivo-full.b8183c5d25e53b8a276c.png" style={{height: 'auto'}}
                                  alt="Impactivo"/>
                         </div>
                     </div>
                                                            <div className="row content">
                                                                    {showThankYou ? (
                                                                        <div className="col-12 text-center" style={{ marginTop: '2rem' }}>
                                                                            <h2 style={{ fontSize: '3rem', fontWeight: 'bold' }}>
                                                                                {assessmentState.batchAssessment?.template?.reportType === "IPATH_ES"
                                                                                    ? "¡Muchas gracias por completar tu Evaluación del TEAM COMPASS!"
                                                                                    : "¡Muchas gracias por completar tu Assessment!"}
                                                                            </h2>
                                                                            <p style={{ fontSize: '1.7rem' }}>Ya puedes cerrar esta ventana con total seguridad.</p>
                                                                            {isThankYouReport ? (
                                                                                <Button
                                                                                    variant="contained"
                                                                                    className="assessment-button"
                                                                                    onClick={handleCompleteAnotherAssessment}
                                                                                >
                                                                                    Completar otra evaluación
                                                                                </Button>
                                                                            ) : null}
                                                                        </div>
                                                                    ) : assessmentState.requestState === REQUEST_STATES.LOADING ? (
                                                                        <div className="col-12 text-center">
                                                                            {/* Blank loading state - no spinner or message shown */}
                                                                        </div>
                                                                    ) : assessmentState.requestState === REQUEST_STATES.ERROR ? (
                                                                        <div className="col-12 text-center error">
                                                                            Assessment not available
                                                                        </div>
                                                                    ) : !assessmentState.batchAssessment ? (
                                                                        <div className="col-12 text-center">
                                                                            {/* Blank state - no assessment found */}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="col-12">
                              {assessmentState.batchAssessment && assessmentState.batchAssessment.assignmentType === "TARGETED" ?
                               <div className="row my-auto">
                                   <div className="col-12 text-center welcome">
                                       Welcome <span className="name"> {assessmentState.batchAssessment.personName}</span>
                                   </div>
                               </div> : null}
                              <div className="row my-auto">
                                  <div className="col-12 text-center message">
                                      {assessmentState.batchAssessment?.template?.reportType === "IPATH_ES"
                                          ? "Tiene la siguiente evaluación disponible"
                                          : "You have the following assessment available"}
                                  </div>
                              </div>
                                  <div className="row">
                                      <div className="col-12 text-center">
                                      {isExcelReport ?
                                       <ExcelSurveyUpload assessment={assessmentState.batchAssessment}
                                                          user={user}
                                                          onUploaded={handleExcelUploaded}/>
                                                                                :
                                      <Button variant="contained"
                                              className="assessment-button"
                                              onClick={() => {setShouldShowAssessment(true)}}>

                                          {assessmentState.batchAssessment?.template?.reportType === "IPATH_ENG" ? "START" : "INICIAR"} {assessmentState.batchAssessment?.assignable?.name || ''}
                                      </Button>
                                      }
                                  </div>
                              </div>
                              {!isExcelReport &&
                               <div className="row my-auto">
                                   <div className="col-8 offset-2 text-center description">
                                       <div
                                           style={{ textAlign: 'justify' }}
                                           dangerouslySetInnerHTML={{
                                               __html: DOMPurify.sanitize(
                                                   assessmentState.batchAssessment?.template?.description || '',
                                                   {
                                                       ALLOWED_TAGS: ['strong', 'em', 'b', 'i', 'u', 'p', 'br', 'ul', 'ol', 'li', 'a'],
                                                       ALLOWED_ATTR: ['href', 'target', 'rel']
                                                   }
                                               )
                                           }}
                                       />
                                   </div>
                               </div>
                              }
                              {assessmentState.batchAssessment?.dateExpires ?
                               <div className="row my-auto">
                                   <div className="col-8 offset-2 text-center">
                                       Expires on: {moment(assessmentState.batchAssessment.dateExpires).format("MM/DD/YYYY")}
                                   </div>
                               </div> : null
                              }
                              {assessmentState.batchAssessment?.dateEnded ?
                               <div className="row my-auto">
                                   <div className="col-8 offset-2 text-center">
                                       Completed on: {moment(assessmentState.batchAssessment.dateEnded).format("MM/DD/YYYY")}
                                   </div>
                               </div> : null
                              }
                                                    </div>
                                                )}
                     </div>
                 </>
                }
            </div>
    );
}

export default withUser(LandingPage);
